import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { alertsApi } from '@/api/clients/alertsApi'
import type { Band } from '@/api/types'
import { BandBadge } from '@/components/common/Badge'
import { Modal } from '@/components/common/Modal'
import { QueryState, EmptyState } from '@/components/common/State'
import {
  useAlertAngles,
  RiskEscalationCard,
  HndlHarvestCard,
  ComplianceDeadlineCard,
  GovernanceDriftCard,
  AlertSummaryCard,
  KIND_ICON,
  KIND_LABEL,
} from '@/components/alerts/alertCards'

type DrillParams = { inventory_id?: string; source_id?: string; kind?: string; limit?: number }

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000))
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const d = Math.floor(hr / 24)
  if (d < 30) return `${d}d ago`
  const mo = Math.floor(d / 30)
  return mo < 12 ? `${mo}mo ago` : `${Math.floor(mo / 12)}y ago`
}

// Modal body: the underlying per-asset alerts for a drill selection.
function AlertDetailList({ orgId, params }: { orgId: string; params: DrillParams }) {
  const location = useLocation()
  const q = useQuery({
    queryKey: ['alerts-detail', orgId, params],
    queryFn: () => alertsApi.list(orgId, params),
    enabled: !!orgId,
    retry: false,
  })
  return (
    <QueryState isLoading={q.isLoading} error={q.error} isEmpty={q.data?.length === 0} emptyProps={{ icon: 'notifications_off', title: 'No alerts' }}>
      <ul className="divide-y divide-border-light dark:divide-border-dark">
        {(q.data ?? []).map((al) => (
          <li key={al.id} className="flex items-center gap-3 py-2.5 text-sm">
            <span className="material-icons text-[18px] text-subtle-light dark:text-subtle-dark shrink-0" title={KIND_LABEL[al.kind] ?? al.kind}>
              {KIND_ICON[al.kind] ?? 'notifications'}
            </span>
            <BandBadge band={al.band as Band} />
            <div className="min-w-0 flex-1">
              {al.asset_id ? (
                <Link to={`/assets/${al.asset_id}`} state={{ backgroundLocation: location }} className="block truncate text-primary dark:text-secondary hover:underline">
                  {al.asset_name}
                </Link>
              ) : (
                <span className="block truncate text-text-light dark:text-text-dark">{al.asset_name}</span>
              )}
              <span className="block truncate text-xs text-subtle-light dark:text-subtle-dark">
                {KIND_LABEL[al.kind] ?? al.kind}
                {al.source_name ? ` · ${al.source_name}` : ''}
                {al.hndl_window != null ? ` · HNDL ${al.hndl_window.toFixed(1)}y` : ''}
              </span>
            </div>
            <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark" title={new Date(al.created_at).toLocaleString()}>
              {timeAgo(al.created_at)}
            </span>
          </li>
        ))}
      </ul>
    </QueryState>
  )
}

// Overview tab: high-level risk rollups (rendered under the Alerts page).
export default function OverviewTab({ orgId }: { orgId: string }) {
  const a = useAlertAngles(orgId)
  const [drill, setDrill] = useState<{ title: string; params: DrillParams } | null>(null)

  return (
    <div>
      {(
        <QueryState isLoading={a.rollup.isLoading} error={a.rollup.error}>
          {/* Priority alert categories */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RiskEscalationCard a={a} />
            <HndlHarvestCard a={a} />
            <ComplianceDeadlineCard a={a} />
            <GovernanceDriftCard a={a} />
          </div>

          <AlertSummaryCard
            a={a}
            className="mt-4"
            onKind={(k) => setDrill({ title: `${KIND_LABEL[k] ?? k} — alerts`, params: { kind: k, limit: 200 } })}
          />

          {/* Breakdown angles */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <div className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark overflow-hidden">
              <div className="px-5 py-3 border-b border-border-light dark:border-border-dark">
                <h2 className="text-sm font-semibold text-text-light dark:text-text-dark">By system</h2>
              </div>
              {a.systems.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left label-sm text-subtle-light dark:text-subtle-dark border-b border-border-light dark:border-border-dark">
                        <th className="px-5 py-2.5 font-medium">Band</th>
                        <th className="px-5 py-2.5 font-medium">System</th>
                        <th className="px-5 py-2.5 font-medium">Crit+High</th>
                        <th className="px-5 py-2.5 font-medium">HNDL</th>
                        <th className="px-5 py-2.5 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-light dark:divide-border-dark">
                      {a.systems.slice(0, 8).map((s) => (
                        <tr
                          key={s.inventory_id || s.name}
                          className="hover:bg-primary/5 cursor-pointer"
                          onClick={() => setDrill({ title: `${s.name} — alerts`, params: { inventory_id: s.inventory_id, limit: 200 } })}
                          title="View underlying asset alerts"
                        >
                          <td className="px-5 py-2.5"><BandBadge band={s.worst_band as Band} /></td>
                          <td className="px-5 py-2.5">
                            {s.inventory_id ? (
                              <Link to={`/inventories/${s.inventory_id}?tab=risk`} onClick={(e) => e.stopPropagation()} className="text-primary dark:text-secondary hover:underline">
                                {s.name}
                              </Link>
                            ) : (
                              <span className="text-subtle-light dark:text-subtle-dark">{s.name}</span>
                            )}
                            {s.business_unit ? <span className="ml-1 text-xs text-subtle-light dark:text-subtle-dark">· {s.business_unit}</span> : null}
                          </td>
                          <td className="px-5 py-2.5 text-text-light dark:text-text-dark">{s.crit_high}</td>
                          <td className="px-5 py-2.5 text-subtle-light dark:text-subtle-dark">{s.hndl_open}</td>
                          <td className="px-5 py-2.5 text-subtle-light dark:text-subtle-dark">{s.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-5">
                  <EmptyState icon="inventory_2" title="No system alerts" />
                </div>
              )}
            </div>

            <div className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark overflow-hidden">
              <div className="px-5 py-3 border-b border-border-light dark:border-border-dark">
                <h2 className="text-sm font-semibold text-text-light dark:text-text-dark">By source</h2>
              </div>
              {a.sources.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left label-sm text-subtle-light dark:text-subtle-dark border-b border-border-light dark:border-border-dark">
                        <th className="px-5 py-2.5 font-medium">Band</th>
                        <th className="px-5 py-2.5 font-medium">Source</th>
                        <th className="px-5 py-2.5 font-medium">System</th>
                        <th className="px-5 py-2.5 font-medium">New</th>
                        <th className="px-5 py-2.5 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-light dark:divide-border-dark">
                      {a.sources.slice(0, 8).map((s) => (
                        <tr
                          key={s.source_id || s.name}
                          className="hover:bg-primary/5 cursor-pointer"
                          onClick={() => setDrill({ title: `${s.name || 'Source'} — alerts`, params: { source_id: s.source_id, limit: 200 } })}
                          title="View underlying asset alerts"
                        >
                          <td className="px-5 py-2.5"><BandBadge band={s.worst_band as Band} /></td>
                          <td className="px-5 py-2.5 text-text-light dark:text-text-dark truncate max-w-[160px]">{s.name || '—'}</td>
                          <td className="px-5 py-2.5 text-subtle-light dark:text-subtle-dark truncate max-w-[140px]">{s.inventory_name || '—'}</td>
                          <td className="px-5 py-2.5">
                            {s.new > 0 ? (
                              <span className="inline-flex items-center rounded-full bg-[#e8734f]/15 px-2 py-0.5 text-xs font-semibold text-[#e8734f]">
                                +{s.new}
                              </span>
                            ) : (
                              <span className="text-xs text-subtle-light dark:text-subtle-dark">0</span>
                            )}
                          </td>
                          <td className="px-5 py-2.5 text-subtle-light dark:text-subtle-dark">{s.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-5">
                  <EmptyState icon="dataset" title="No source alerts" />
                </div>
              )}
            </div>
          </div>
        </QueryState>
      )}

      {drill && (
        <Modal title={drill.title} onClose={() => setDrill(null)} wide>
          <AlertDetailList orgId={orgId} params={drill.params} />
        </Modal>
      )}
    </div>
  )
}
